import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Building2, FileText, Users, Briefcase, BarChart3, Clock, 
  CheckCircle2, Loader2, ArrowRight, Shield, Plus, Eye, 
  TrendingUp, Scale, Sparkles, Gavel, Search, Calculator,
  FolderOpen, ChevronRight, MapPin, Activity, Target,
  Zap, BookOpen, Globe, MessageSquare, Truck
} from "lucide-react";
import { API } from "@/constants";
import { useAuth } from "@/contexts/AuthContext";

const CompanyDashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/dashboard/overview`, { withCredentials: true });
        setData(res.data);
      } catch (e) {
        console.error(e);
        setData({ stats: {}, recent_cases: [], recent_reports: [], recent_scorecards: [], recent_investigations: [] });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
    </div>
  );

  const stats = data?.stats || {};
  const ps = stats.practice_suite || {};

  const QUICK_TOOLS = [
    { to: "/tools/case-starter", label: "Start New Case", icon: Plus, color: "bg-blue-600 text-white" },
    { to: "/tools/investigator", label: "Investigate", icon: Eye, color: "bg-red-600 text-white" },
    { to: "/tools/opening", label: "Opening Statement", icon: Gavel, color: "bg-purple-600 text-white" },
    { to: "/tools/demand-letter", label: "Demand Letter", icon: FileText, color: "bg-emerald-600 text-white" },
    { to: "/tools/carrier-scorecard", label: "Carrier Intel", icon: Shield, color: "bg-amber-600 text-white" },
    { to: "/tools/judges", label: "Judge Analytics", icon: BarChart3, color: "bg-teal-600 text-white" },
  ];

  const TOOL_SECTIONS = [
    {
      title: "Case Management & Trial",
      tools: [
        { to: "/tools/litix-wizard", label: "Litix Wizard", icon: Sparkles },
        { to: "/tools/case-builder", label: "Case Builder", icon: Briefcase },
        { to: "/tools/opening", label: "Opening Statement", icon: Gavel },
        { to: "/tools/closing", label: "Closing Argument", icon: BookOpen },
        { to: "/tools/trial-organizer", label: "Trial Organizer", icon: FolderOpen },
        { to: "/tools/deposition", label: "Depositions", icon: Users },
      ]
    },
    {
      title: "Settlement & Documents",
      tools: [
        { to: "/tools/settlement-negotiation", label: "Settlement Strategy", icon: Scale },
        { to: "/tools/demand-letter", label: "Demand Letter", icon: FileText },
        { to: "/tools/mediation", label: "Mediation Brief", icon: MessageSquare },
        { to: "/tools/motion", label: "Motion Practice", icon: BookOpen },
        { to: "/tools/insurance-report", label: "Insurance Report", icon: Shield },
        { to: "/tools/case-summary", label: "Case Summary", icon: FileText },
      ]
    },
    {
      title: "Intelligence & Analytics",
      tools: [
        { to: "/tools/investigator", label: "Investigator", icon: Eye },
        { to: "/tools/request-center", label: "Request Center", icon: Search },
        { to: "/tools/medical-intel", label: "Medical Intel", icon: Activity },
        { to: "/tools/carrier-scorecard", label: "Carrier Scorecard", icon: Shield },
        { to: "/tools/judges", label: "Judge Analytics", icon: BarChart3 },
        { to: "/tools/precedent-finder", label: "Precedent Finder", icon: Search },
      ]
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="company-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-900 via-teal-900 to-emerald-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <Building2 className="h-7 w-7 text-emerald-300" />
                <h1 className="text-xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>{user?.firm_name || "Firm Dashboard"}</h1>
              </div>
              <p className="text-sm text-emerald-300">Welcome back, {user?.name}</p>
            </div>
            <Link to="/tools/litix-wizard">
              <Button className="bg-emerald-600 hover:bg-emerald-500" data-testid="new-case-btn">
                <Plus className="h-4 w-4 mr-1.5" /> New Case
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Quick Launch */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
          {QUICK_TOOLS.map((t, i) => (
            <Link key={i} to={t.to}>
              <div className={`${t.color} rounded-lg p-3 text-center hover:opacity-90 transition-opacity cursor-pointer`} data-testid={`quick-${t.label.toLowerCase().replace(/ /g,'-')}`}>
                <t.icon className="h-5 w-5 mx-auto mb-1" />
                <p className="text-xs font-medium">{t.label}</p>
              </div>
            </Link>
          ))}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Cases", value: stats.total_cases || 0, icon: Briefcase, color: "text-blue-600" },
            { label: "AI Outputs", value: stats.total_ai_outputs || 0, icon: Sparkles, color: "text-purple-600" },
            { label: "Scorecards", value: stats.total_scorecards || 0, icon: Shield, color: "text-amber-600" },
            { label: "Investigations", value: stats.total_investigations || 0, icon: Eye, color: "text-red-600" },
          ].map((s, i) => (
            <Card key={i} className="border-0 shadow-sm">
              <CardContent className="pt-3 pb-3 px-4 flex items-center gap-3">
                <s.icon className={`h-5 w-5 ${s.color}`} />
                <div>
                  <p className="text-xl font-bold">{s.value}</p>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">{s.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tool Sections */}
        <div className="grid md:grid-cols-3 gap-4">
          {TOOL_SECTIONS.map((section, si) => (
            <Card key={si} className="border-0 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">{section.title}</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-1">
                  {section.tools.map((tool, ti) => (
                    <Link key={ti} to={tool.to}>
                      <div className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer" data-testid={`tool-${tool.label.toLowerCase().replace(/ /g,'-')}`}>
                        <tool.icon className="h-4 w-4 text-slate-400" />
                        <span className="text-sm">{tool.label}</span>
                        <ChevronRight className="h-3 w-3 text-slate-300 ml-auto" />
                      </div>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Recent Cases & Scorecards */}
        <div className="grid lg:grid-cols-2 gap-6">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-blue-600" /> Recent Cases
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {(data?.recent_cases || []).length === 0 ? (
                <div className="text-center py-6">
                  <FolderOpen className="h-8 w-8 text-slate-200 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">No cases yet</p>
                  <Link to="/tools/litix-wizard">
                    <Button size="sm" className="mt-3"><Plus className="h-3 w-3 mr-1" />Start First Case</Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-2">
                  {data.recent_cases.map((c, i) => (
                    <Link key={i} to={`/case/${c.case_id}`}>
                      <div className="flex items-center justify-between p-2.5 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">
                        <div>
                          <p className="text-sm font-medium">{c.case_name || "Untitled"}</p>
                          <p className="text-[10px] text-slate-400">{c.state}{c.court_type ? ` - ${c.court_type}` : ""}</p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-slate-300" />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="h-4 w-4 text-purple-600" /> Practice Suite Usage
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: "Statements", value: ps.statements || 0 },
                  { label: "Settlements", value: ps.settlements || 0 },
                  { label: "Demand Letters", value: ps.demand_letters || 0 },
                  { label: "Depositions", value: ps.depositions || 0 },
                  { label: "Motions", value: ps.motions || 0 },
                  { label: "Mediations", value: ps.mediations || 0 },
                ].map((item, i) => (
                  <div key={i} className="p-2 rounded-lg bg-slate-50">
                    <p className="text-xs text-slate-500">{item.label}</p>
                    <p className="text-lg font-bold">{item.value}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CompanyDashboard;
